import React from 'react';
import * as R from 'ramda';
import { OuterClick } from 'react-outer-click';
import { pure, compose, withState, withHandlers, withPropsOnChange } from 'react-recompose';
// components
import { TextComponent } from '../../components/text';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// hocs
import { withComponentDidUpdatePropCallback } from '../../hocs/common';
// forms
import { MultiEmailInput } from './ui';
// ui
import { Box, Flex, RelativeBox } from '../../ui';
//////////////////////////////////////////////////

const isInList = (currentEmail, emails) => R.includes(currentEmail, emails);

const isEmail = (email) => GC.EMAIL_REGEXP.test(email);

const isValid = (props, inputValue) => {
  const { emails, setError } = props;

  let error = null;

  if (isInList(inputValue, emails)) {
    error = `
      ${inputValue}
      ${G.getWindowLocale('titles:has-already-been-added', 'has already been added')}
    `;
  }

  if (R.not(isEmail(inputValue))) {
    error = `
      ${inputValue}
      ${G.getWindowLocale('titles:is-not-email', 'is not a valid email address')}
    `;
  }

  if (G.isNotNilAndNotEmpty(error)) {
    setError(error);

    return false;
  }

  return true;
};

const maxWidthCalc = (value) => {
  if (G.isNumber(value)) {
    return `calc(${value}px - 41px)`;
  }

  return `calc(${value} - 41px)`;
};

const addNewEmail = (props) => {
  const { emails, inputValue, setEmails, setInputValue } = props;

  const inputValueToUse = R.trim(inputValue);

  if (G.isNotNilAndNotEmpty(inputValueToUse)) {
    if (isValid(props, inputValueToUse)) {
      setInputValue('');
      setEmails(R.append(inputValueToUse, emails));
    }
  }
};

const enhance = compose(
  withState('error', 'setError', null),
  withState('inputValue', 'setInputValue', ''),
  withState('emails', 'setEmails', ({ value }) =>
    G.ifElse(
      G.isNotNilAndNotEmpty(value),
      G.ifElse(G.isArray(value), value, R.of(Array, value)),
      [],
    ),
  ),
  withHandlers({
    handleKeyDown: (props) => (event) => {
      const eventKeyCodes = [GC.EVENT_KEY_CODE_TAB, GC.EVENT_KEY_CODE_ENTER, GC.EVENT_KEY_CODE_COMMA];
      if (R.includes(R.prop('keyCode', event), eventKeyCodes)) {
        event.preventDefault();

        addNewEmail(props);
      }
    },
    handleChangeEmailInput: ({ setError, setInputValue }) => (event) => {
      setError(null);
      setInputValue(G.getEventTargetValue(event));
    },
    handleDeleteEmail: ({ emails, setEmails }) => (email) =>
      setEmails(R.filter((item) => G.notEquals(item, email), emails)),
    handlePaste: (props) => (event) => {
      event.preventDefault();

      const { emails, setEmails } = props;

      const paste = R.prop('clipboardData', event).getData('text');
      const emailsToUse = R.uniq(paste.match(GC.EMAIL_REGEXP_GLOBAL));

      if (G.isNotNilAndNotEmpty(emailsToUse)) {
        const toBeAdded = R.filter((email) => R.not(isInList(email, emails)), emailsToUse);

        setEmails(R.concat(emails, toBeAdded));
      }
    },
    handleOverrideEmails: (props) => () => {
      const { value, setEmails, shouldOverrideEmails } = props;

      if (G.isTrue(shouldOverrideEmails)) {
        const emails = G.ifElse(
          G.isNotNilAndNotEmpty(value),
          G.ifElse(G.isArray(value), value, R.of(Array, value)),
          [],
        );

        setEmails(emails);
      }
    },
    handleOnOuterClick: (props) => () => addNewEmail(props),
  }),
  withPropsOnChange(['error'], ({ id, error, setFieldError }) => setFieldError(id, error)),
  withPropsOnChange(['emails'], ({ id, emails, setFieldValue }) => setFieldValue(id, emails)),
  withComponentDidUpdatePropCallback({
    propName: 'value',
    callbackName: 'handleOverrideEmails',
  }),
  pure,
);

export const MultiEmailInputComponent = (props) => {
  const {
    id,
    width,
    emails,
    minHeight,
    inputValue,
    borderColor,
    handlePaste,
    borderRadius,
    handleKeyDown,
    setFieldTouched,
    handleDeleteEmail,
    handleOnOuterClick,
    handleChangeEmailInput,
  } = props;

  return (
    <Flex
      p='5px 10px'
      width={width}
      height='auto'
      flexWrap='wrap'
      border='1px solid'
      borderRadius={borderRadius}
      minHeight={R.or(minHeight, 30)}
      background={G.getTheme('colors.white')}
      borderColor={R.or(borderColor, G.getTheme('forms.inputs.borderColor'))}
    >
      {emails.map((item, index) => (
        <Flex
          mr={10}
          my='5px'
          key={index}
          fontSize={12}
          color={G.getTheme('colors.light.blue')}
        >
          <TextComponent
            key={index}
            title={item}
            fontSize={12}
            withEllipsis={true}
            maxWidth={maxWidthCalc(width)}
          >
            {item}
          </TextComponent>
          <Box
            ml='3px'
            px='4px'
            fontSize={14}
            cursor='pointer'
            borderRadius='50%'
            color={G.getTheme('colors.light.grey')}
            onClick={() => handleDeleteEmail(item)}
            border={`1px solid ${G.getTheme('colors.light.blue')}`}
          >
            &times;
          </Box>
        </Flex>
      ))}
      <OuterClick width='100%' as={RelativeBox} onOuterClick={handleOnOuterClick}>
        <MultiEmailInput
          id={id}
          width='100%'
          value={inputValue}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onChange={handleChangeEmailInput}
          onBlur={() => G.isFunction(setFieldTouched) && setFieldTouched(id, true, false)}
          placeholder={G.getWindowLocale('titles:enter-email-and-press-enter', 'Enter email and press Enter')}
        />
      </OuterClick>
    </Flex>
  );
};

export default enhance(MultiEmailInputComponent);
